import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import './CountryFlag.scss';
import { useSelector } from "react-redux";

// Some API country codes are not valid ISO codes for flagcdn
const FLAG_CODE_OVERRIDES = {
  TP: "TL",
  XG: "GB",
  XA: "AU",
  XU: "GG",
  XJ: "JE",
  XM: "IM",
  AN: "NL",
  YU: null,
};

const getFlagCode = (sortname) => {
  if (!sortname) return null;
  const upper = sortname.toUpperCase();
  if (FLAG_CODE_OVERRIDES[upper] === null) return null;
  return FLAG_CODE_OVERRIDES[upper] || upper;
};

const getLocalizedCountryName = (sortname, fallbackName, language) => {
  const flagCode = getFlagCode(sortname);
  if (!flagCode) return fallbackName;

  const locale = language === "Italian" ? "it" : "en";
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: "region" });
    return displayNames.of(flagCode) || fallbackName;
  } catch {
    return fallbackName;
  }
};

const FlagImage = ({ code, name, className, fallbackText }) => {
  const [hasError, setHasError] = useState(false);
  const flagCode = getFlagCode(code);

  if (!flagCode || hasError) {
    return (
      <span className="flag-fallback-text" title={name}>
        {fallbackText || code}
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/w40/${flagCode.toLowerCase()}.png`}
      alt={name}
      className={className}
      onError={() => setHasError(true)}
    />
  );
};

const CountryFlag = ({ setPhoneCode, language }) => {
  const [selectedCountry, setSelectedCountry] = useState({
    id: "107",
    name: "Italy",
    code: "IT",
    phoneCode: "+39",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const currentLanguage = language || localStorage.getItem("language") || "English";
  const isItalian = currentLanguage === "Italian";

  const { countries } = useSelector((state) => state.surveyInfo);

  const memoizedCountries = useMemo(() => {
    return countries?.map((country) => {
      const displayName = getLocalizedCountryName(country.sortname, country.name, currentLanguage);
      return {
        id: country.id,
        name: displayName,
        code: country.sortname,
        phoneCode: `+${country.phonecode}`,
      };
    }) || [];
  }, [countries, currentLanguage]);

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return memoizedCountries;

    const query = searchQuery.toLowerCase();
    return memoizedCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query) ||
        country.phoneCode.includes(query)
    );
  }, [memoizedCountries, searchQuery]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchQuery("");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (memoizedCountries.length === 0) return;

    const italy = memoizedCountries.find((country) => country.code === "IT");
    setSelectedCountry((prev) => {
      const updated = memoizedCountries.find((country) => country.code === prev.code);
      return updated || italy || prev;
    });
  }, [memoizedCountries]);

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery("");
    setPhoneCode(country.phoneCode);
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      if (prev) setSearchQuery("");
      return !prev;
    });
  };

  return (
    <div className="flag-input-container" ref={dropdownRef}>
      <div className="flag-input-box" onClick={toggleDropdown}>
        <div className="flag-select">
          <FlagImage
            code={selectedCountry.code}
            name={selectedCountry.name}
            className="flag-icon"
            fallbackText={selectedCountry.code}
          />
        </div>
        <span className="arrow">▼</span>
      </div>

      {isOpen && (
        <div className="dropdown">
          <input
            type="text"
            className="search-box"
            placeholder={isItalian ? "Cerca paese" : "Search country"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <ul>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <li key={country.id} onClick={() => handleSelectCountry(country)}>
                  <FlagImage
                    code={country.code}
                    name={country.name}
                    className="flag-icon"
                    fallbackText={country.code}
                  />
                  <span className="country-name">{country.name}</span>
                  <span className="phone-code">{country.phoneCode}</span>
                </li>
              ))
            ) : (
              <li className="no-results">
                {isItalian ? "Nessun paese trovato" : "No country found"}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryFlag;
